/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import classnames from 'classnames';
import * as React from 'react';

import * as MsDyn365 from '@msdyn365-commerce/core';
import { Button, Alert, IModuleProps, Modal, ModalBody, ModalHeader } from '@msdyn365-commerce-modules/utilities';
import { promoteToSellerAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/BusinessPartnersDataActions.g';
import { BusinessPartnerUser } from '@msdyn365-commerce/retail-proxy';
import { ObjectExtensions, StringExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { Text } from '@msdyn365-commerce/core';
import { IBusinessPartnerPromotionData } from './business-partner-promotion.data';
import { IBusinessPartnerPromotionProps } from './business-partner-promotion.props.autogenerated';
import { BusinessPartnerPromotionTermsParagraph } from './components';

export interface IBusinessPartnerPromotionViewProps extends IBusinessPartnerPromotionProps<IBusinessPartnerPromotionData> {
    businessPartnerPromotionProps: IModuleProps;
    heading: React.ReactNode;
    notificationMessage?: React.ReactNode;
    sellerDescriptions: React.ReactNode[];
    termsOfServiceCheckbox: React.ReactNode;
    termsOfServiceModal: React.ReactNode;
    submitButton: React.ReactNode;
}

export interface IBusinessPartnerPromotionState {
    showTermsOfService: boolean;
    showSubmitButton: boolean;
    termsOfServiceChecked: boolean;
    termsOfServiceModalOpened: boolean;
    errorMessage?: string;
    notificationMessage?: string;
}

/**
 * The class name root for the module.
 */
export const moduleClassName: string = 'ms-business-partner-promotion';

/**
 *
 * BusinessPartnerPromotion component
 * @extends {React.PureComponent<IBusinessPartnerPromotionProps<IBusinessPartnerPromotionData>>}
 */
class BusinessPartnerPromotion extends React.Component<
    IBusinessPartnerPromotionProps<IBusinessPartnerPromotionData>,
    IBusinessPartnerPromotionState
> {
    constructor(props: IBusinessPartnerPromotionProps<IBusinessPartnerPromotionData>) {
        super(props);
        this.state = {
            showTermsOfService: true,
            showSubmitButton: true,
            termsOfServiceChecked: false,
            termsOfServiceModalOpened: false,
            errorMessage: undefined,
            notificationMessage: undefined
        };
    }

    public render(): JSX.Element | null {
        const {
            showSubmitButton,
            showTermsOfService,
            termsOfServiceChecked,
            termsOfServiceModalOpened,
            errorMessage,
            notificationMessage
        } = this.state;
        const { resources, data, config } = this.props;
        const { heading, termsOfService } = config;
        const { authorizationErrorMessage } = resources;
        const { b2bUserInformation } = data;
        const canSubmit = this.isAdminUser(b2bUserInformation.result);
        let error: string | undefined = errorMessage;

        // do not show error message when the API is still in loading.
        if (!canSubmit && b2bUserInformation.status !== 'LOADING') {
            error = authorizationErrorMessage;
        }
        const btnEnabled = canSubmit && termsOfServiceChecked;

        const viewProps: IBusinessPartnerPromotionViewProps = {
            ...this.props,
            businessPartnerPromotionProps: {
                moduleProps: this.props,
                className: classnames(moduleClassName, moduleClassName)
            },
            heading: <Text className={`${moduleClassName}__heading`} {...heading} />,
            notificationMessage: this.renderNotificationBar(error, notificationMessage),
            sellerDescriptions: this.renderSellerDescriptions(),
            termsOfServiceCheckbox: showTermsOfService && (
                <div className={`${moduleClassName}__agreement`}>
                    <input
                        className={`${moduleClassName}__agreement-checkbox`}
                        type='checkbox'
                        aria-label={resources.termsOfServiceLinkText}
                        onChange={this.onCheckBoxChange}
                    />
                    <span className={`${moduleClassName}__agreement-text`}>{resources.termsOfServiceAgreementText}</span>
                    <a className={`${moduleClassName}__agreement-link`} role='button' onClick={this.onToggleTermOfServiceModal}>
                        {resources.termsOfServiceLinkText}
                    </a>
                </div>
            ),
            termsOfServiceModal: (
                <Modal
                    className={`${moduleClassName}__modal`}
                    isOpen={termsOfServiceModalOpened}
                    horizontalPosition='center'
                    verticalPosition='center'
                    autoFocus
                    fade={false}
                    toggle={this.onToggleTermOfServiceModal}
                    aria-label={resources.termsOfServiceLinkText}
                >
                    <ModalHeader toggle={this.onToggleTermOfServiceModal}>{resources.termsOfServiceModalHeading}</ModalHeader>
                    <ModalBody>
                        <MsDyn365.RichTextComponent text={termsOfService} />
                    </ModalBody>
                </Modal>
            ),
            submitButton: showSubmitButton && (
                <div className={`${moduleClassName}__submit-container`}>
                    <Button
                        onClick={this.onSubmit}
                        className={`${moduleClassName}__submit-container-button`}
                        role='button'
                        aria-label={resources.submitRequestLabel}
                        title={resources.submitRequestLabel}
                        disabled={!btnEnabled}
                        aria-disabled={!btnEnabled}
                    >
                        {resources.submitRequestLabel}
                    </Button>
                </div>
            )
        };

        return this.props.renderView(viewProps);
    }

    private renderNotificationBar(errorMessage: string | undefined, notificationMessage: string | undefined): React.ReactNode {
        let notificationBar: React.ReactNode = null;
        if (errorMessage) {
            notificationBar = <Alert color='danger'>{errorMessage}</Alert>;
        } else if (notificationMessage) {
            notificationBar = <Alert color='success'>{notificationMessage}</Alert>;
        }
        return notificationBar;
    }

    private renderSellerDescriptions(): React.ReactNode[] {
        const { config } = this.props;
        const results: React.ReactNode[] = [];
        const sellerDescriptions = config.sellerDescriptions;

        if (!sellerDescriptions) {
            return results;
        }

        return sellerDescriptions.map((description, idx1) => {
            return (
                <div key={idx1} className={`${moduleClassName}__desc-container`}>
                    <Text className={`${moduleClassName}__desc-heading`} text={description.heading} />
                    <ul>
                        {description.bulletPoints?.map((bulletPoint, idx2) => {
                            return (
                                <BusinessPartnerPromotionTermsParagraph
                                    key={idx2}
                                    className={`${moduleClassName}__desc-paragraph`}
                                    titleClassName={`${moduleClassName}__desc-title`}
                                    title={bulletPoint.label}
                                    description={bulletPoint.text}
                                />
                            );
                        })}
                    </ul>
                </div>
            );
        });
    }

    private isAdminUser(user: BusinessPartnerUser | undefined): boolean {
        return (
            !ObjectExtensions.isNullOrUndefined(user) && !StringExtensions.isNullOrEmpty(user.BusinessPartnerId) && user.IsAdmin === true
        );
    }

    private readonly onCheckBoxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({
            termsOfServiceChecked: event.target.checked
        });
    };

    private readonly onToggleTermOfServiceModal = () => {
        this.setState({
            termsOfServiceModalOpened: !this.state.termsOfServiceModalOpened
        });
    };

    private readonly onSubmit = async (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        const { context, data, resources } = this.props;
        const { errorMessageText, promoteToSellerSubmitSuccessMessage } = resources;
        const businessPartnerId = data.b2bUserInformation.result?.BusinessPartnerId ?? '';

        try {
            await promoteToSellerAsync({ callerContext: context.actionContext }, businessPartnerId);
            this.setState({
                showSubmitButton: false,
                showTermsOfService: false,
                errorMessage: undefined,
                notificationMessage: promoteToSellerSubmitSuccessMessage
            });
        } catch {
            this.setState({
                showTermsOfService: true,
                showSubmitButton: true,
                errorMessage: errorMessageText,
                notificationMessage: undefined
            });
        }
    };
}

export default BusinessPartnerPromotion;
